import React, { useCallback, useState } from "react";
import EditIcon from "@mui/icons-material/Edit";

import { MachineEditorDialog } from "./MachineEditorDialog";
import { MachineConfiguration } from "./machineEditor.typing";

interface EditMachineButtonProps {
  machineId: string;
  /** Lets the log header repaint its title without waiting for a refetch. */
  onSaved?: (machine: MachineConfiguration) => void;
}

/**
 * The pen next to a machine's name and code in the log header. Opens the same popover as the "+",
 * prefilled with the machine's current configuration.
 */
export const EditMachineButton: React.FC<EditMachineButtonProps> = ({
  machineId,
  onSaved,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setIsOpen(true);
    },
    []
  );

  const close = useCallback(() => setIsOpen(false), []);

  if (!machineId) {
    return null;
  }

  return (
    <>
      {/* A bare 20px icon is under the ~44px a thumb can reliably hit, and this sits beside a
          machine name that can run to two lines, so it gets its own padded target rather than
          being squeezed in with the text. */}
      <span
        className="pointer"
        role="button"
        aria-label="Edit machine"
        title="Edit machine"
        onClick={open}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "44px",
          height: "44px",
          minWidth: "44px",
          marginLeft: "4px",
          marginTop: "-10px",
          flexShrink: 0,
          opacity: 0.85,
        }}
      >
        <EditIcon style={{ fontSize: "20px" }} />
      </span>

      <MachineEditorDialog
        open={isOpen}
        mode="edit"
        machineId={machineId}
        onClose={close}
        onSaved={onSaved}
      />
    </>
  );
};
